import {
  CircleDollarSign,
  CodeXml,
  CopyPlus,
  Dock,
  FileImage,
  Filter,
  Gift,
  LayoutPanelTop,
  LucideIcon,
  MessageSquareText,
  Newspaper,
  Palette,
  PencilRuler,
  Shapes,
  ShieldCheck,
  Shirt,
  ShoppingBasket,
  ShoppingCart,
  Split,
  SquareArrowUp,
  SquareStack,
  Tag,
  TextInitial,
  Timer,
  Truck,
} from "lucide-react";

import {
  ScratchCard as ScratchCardType,
  ScratchOfferCard,
} from "@intelligems/ig-types";
import { Theme } from "@mui/material";
import {
  CHECKOUT_DESCRIPTION,
  OFFER_DESCRIPTION,
  ONSITE_EDITS_DESCRIPTION,
  PRICING_DESCRIPTION,
  SHIPPING_DESCRIPTION,
  SPLIT_URL_DESCRIPTION,
  TEMPLATE_DESCRIPTION,
  THEME_DESCRIPTION,
} from "@src/constants/experimentDescriptions";

type OfferCard = ScratchOfferCard & { iconComponent: LucideIcon };

export const getOfferCards = (theme: Theme): OfferCard[] => {
  const cards: OfferCard[] = [
    {
      title: "Amount off Products",
      description: "Run % or $ off product discounts",
      discountType: "cartDiscount",
      discountMethod: "perItem",
      modType: "cartDiscount",
      id: "cart-discount-products",
      iconComponent: Shirt,
      iconColor: theme.igPrimitives.coral[900],
      iconBackgroundColor: theme.igPrimitives.coral[100],
      backgroundColor: theme.igPrimitives.coral[50],
    },
    {
      title: "Amount off Order",
      description: "Offer % or $ off subtotal/cart",
      discountType: "cartDiscount",
      discountMethod: "perOrder",
      modType: "cartDiscount",
      id: "cart-discount-order",
      iconComponent: ShoppingBasket,
      iconColor: theme.igPrimitives.pink[900],
      iconBackgroundColor: theme.igPrimitives.pink[100],
      backgroundColor: theme.igPrimitives.pink[50],
    },
    {
      title: "Volume Discount",
      description: "Discount based on spend or quantity",
      discountType: "tieredDiscount",
      modType: "tieredDiscount",
      id: "volume-discount",
      iconComponent: SquareStack,
      iconColor: theme.igPrimitives.azure["A300"],
      iconBackgroundColor: theme.igPrimitives.azure[200],
      backgroundColor: theme.igPrimitives.blue[50],
    },
    {
      title: "Free Shipping",
      description: "Free shipping with purchase minimum",
      discountType: "freeShipping",
      modType: "freeShipping",
      id: "free-shipping",
      iconComponent: Truck,
      iconColor: theme.igPrimitives.green["A300"],
      iconBackgroundColor: theme.igPrimitives.green[100],
      backgroundColor: theme.igPrimitives.green[50],
    },
    {
      title: "Free Gift",
      description: "Free gift with qualifying purchase",
      discountType: "freeGift",
      modType: "freeGift",
      id: "free-gift",
      iconComponent: Gift,
      iconColor: theme.igPrimitives.purple[900],
      iconBackgroundColor: theme.igPrimitives.purple[100],
      backgroundColor: theme.igPrimitives.purple[50],
    },
    {
      title: "Buy X Get Y",
      description: "Buy X, get Y free or discounted.",
      discountType: "buyXGetY",
      modType: "buyXGetY",
      unitType: "unit",
      id: "buy-x-get-y",
      iconComponent: CopyPlus,
      iconColor: theme.igPrimitives.teal[800],
      iconBackgroundColor: theme.igPrimitives.teal[100],
      backgroundColor: theme.igPrimitives.teal[50],
    },
  ];

  return cards;
};

export const getOverviewCards = (
  theme: Theme,
  hasPriceModAccess: boolean,
  shouldDuplicateProducts?: boolean
): (ScratchCardType & { iconComponent: LucideIcon })[] => [
  {
    title: "Redirects",
    description: "Redirect audiences from one URL to another",
    modType: "splitUrl",
    id: "split-url",
    iconComponent: Split,
    iconColor: theme.igPrimitives.coral[900],
    iconBackgroundColor: theme.igPrimitives.coral[100],
    backgroundColor: theme.igPrimitives.coral[50],
  },
  {
    title: "Content Edits",
    description: "Edit text, images, and more on specific pages",
    modType: "onsiteEdits",
    id: "onsite-edits",
    iconComponent: PencilRuler,
    iconColor: theme.igPrimitives.pink[900],
    iconBackgroundColor: theme.igPrimitives.pink[100],
    backgroundColor: theme.igPrimitives.pink[50],
  },
  {
    title: "Styles & JS",
    description: "Inject CSS and javascript to modify functionality",
    modType: "onsiteInjections",
    id: "onsite-injections",
    iconComponent: CodeXml,
    iconColor: theme.igPrimitives.azure["A300"],
    iconBackgroundColor: theme.igPrimitives.azure[300],
    backgroundColor: theme.igPrimitives.azure[50],
  },
  {
    title: "Theme Change",
    description: "Change appearance by swapping your Shopify theme",
    modType: "theme",
    id: "theme-change",
    iconComponent: Palette,
    iconColor: theme.igPrimitives.green["A300"],
    iconBackgroundColor: theme.igPrimitives.green[200],
    backgroundColor: theme.igPrimitives.green[50],
  },
  {
    title: "Template Change",
    description: "Overhaul pages by swapping Shopify templates",
    modType: "template",
    id: "template-changes",
    iconComponent: LayoutPanelTop,
    iconColor: theme.igPrimitives.purple[900],
    iconBackgroundColor: theme.igPrimitives.purple[100],
    backgroundColor: theme.igPrimitives.purple[50],
  },
  {
    title: "Price Change",
    description: "Make specific price changes for products",
    modType: "price",
    id: "price-change",
    iconComponent: CircleDollarSign,
    iconColor: hasPriceModAccess
      ? theme.igPrimitives.violet[500]
      : theme.palette.text.interactive.neutral.disabled,
    iconBackgroundColor: hasPriceModAccess
      ? theme.igPrimitives.violet[100]
      : theme.igBackgroundColors.interactive.neutral.disabled,
    backgroundColor:
      hasPriceModAccess || shouldDuplicateProducts === true
        ? theme.igPrimitives.violet[50]
        : theme.igBackgroundColors.interactive.neutral.default,
    disabled: !hasPriceModAccess || shouldDuplicateProducts === true,
    tooltipText: !hasPriceModAccess
      ? "This feature requires a Plus plan or above"
      : shouldDuplicateProducts === true
        ? "Duplicate products are not supported for experiences"
        : "",
  },
];

export const getCheckoutBlockCards = (
  theme: Theme
): (ScratchCardType & {
  iconComponent: LucideIcon;
})[] => [
  {
    title: "Upsell",
    description: "Recommend products to add at checkout",
    modType: "checkoutBlock",
    id: "upsell",
    iconComponent: SquareArrowUp,
    iconColor: theme.igPrimitives.purple[800],
    iconBackgroundColor: theme.igPrimitives.purple[100],
    backgroundColor: theme.igPrimitives.purple[50],
    checkoutBlockType: "upsell",
  },
  {
    title: "Trust badge",
    description: "Create badges with icons, images, and text",
    modType: "checkoutBlock",
    id: "trust-badge",
    iconComponent: ShieldCheck,
    iconColor: theme.igPrimitives.lime["A100"],
    iconBackgroundColor: theme.igPrimitives.lime[100],
    backgroundColor: theme.igPrimitives.lime[50],
    checkoutBlockType: "trustBadge",
  },
  {
    title: "Shipping progress bar",
    description: "Motivate buyers to add more to unlock a reward",
    modType: "checkoutBlock",
    id: "shipping-progress-bar",
    iconComponent: Truck,
    iconColor: theme.igPrimitives.teal[800],
    iconBackgroundColor: theme.igPrimitives.teal[100],
    backgroundColor: theme.igPrimitives.teal[50],
    checkoutBlockType: "shippingProgressBar",
  },
  {
    title: "Call out",
    description: "Draw attention with a single badge message",
    modType: "checkoutBlock",
    id: "callout",
    iconComponent: MessageSquareText,
    iconColor: theme.igPrimitives.azure["A300"],
    iconBackgroundColor: theme.igPrimitives.azure[100],
    backgroundColor: theme.igPrimitives.azure[50],
    checkoutBlockType: "trustBadge",
    checkoutBlockTemplate: "callOut",
  },
  {
    title: "Static image",
    description: "Upload and display trust badge images",
    modType: "checkoutBlock",
    id: "static-image",
    iconComponent: FileImage,
    iconColor: theme.igPrimitives.coral[900],
    iconBackgroundColor: theme.igPrimitives.coral[100],
    backgroundColor: theme.igPrimitives.coral[50],
    checkoutBlockType: "trustBadge",
    checkoutBlockTemplate: "images",
  },
  {
    title: "Brand logo",
    description: "Upload and display a different logo at checkout",
    modType: "checkoutBlock",
    id: "brand-logo",
    iconComponent: Dock,
    iconColor: theme.igPrimitives.green["A300"],
    iconBackgroundColor: theme.igPrimitives.green[200],
    backgroundColor: theme.igPrimitives.green[50],
    checkoutBlockType: "trustBadge",
    checkoutBlockTemplate: "brandLogo",
  },
  {
    title: "Countdown timer",
    description: "Add urgency with a live countdown",
    modType: "checkoutBlock",
    id: "timer",
    iconComponent: Timer,
    iconColor: theme.igPrimitives.rose[800],
    iconBackgroundColor: theme.igPrimitives.rose[100],
    backgroundColor: theme.igPrimitives.rose[50],
    checkoutBlockType: "timer",
  },
  {
    title: "Text only",
    description: "Draw attention with a single message",
    modType: "checkoutBlock",
    id: "text-only",
    iconComponent: TextInitial,
    iconColor: theme.igPrimitives.pink["900"],
    iconBackgroundColor: theme.igPrimitives.pink[200],
    backgroundColor: theme.igPrimitives.pink[50],
    checkoutBlockType: "trustBadge",
    checkoutBlockTemplate: "textOnly",
  },
];

export const getPostPurchaseCards = (
  theme: Theme
): (ScratchCardType & {
  iconComponent: LucideIcon;
  comingSoon?: boolean;
})[] => [
  {
    title: "Post-purchase upsell",
    description: "Create a one-step offer after checkout",
    modType: "postPurchase",
    id: "post-purchase-upsell",
    iconComponent: ShoppingCart,
    iconColor: theme.igPrimitives.pink[900],
    iconBackgroundColor: theme.igPrimitives.pink[100],
    backgroundColor: theme.igPrimitives.pink[50],
  },
  {
    title: "Post-purchase funnel",
    description: "Tell us you're interested in this feature",
    modType: "postPurchase",
    id: "post-purchase-funnel",
    iconComponent: Filter,
    iconColor: theme.igPrimitives.coral[900],
    iconBackgroundColor: theme.igPrimitives.coral[100],
    backgroundColor: theme.igPrimitives.coral[50],
    comingSoon: true,
    disabled: true,
  },
];

export const getExperimentCards = (
  theme: Theme,
  hasCorrectCheckoutPlan?: boolean
): (ScratchCardType & {
  iconComponent: LucideIcon;
})[] => [
  {
    title: "Content: Onsite Edits",
    description: ONSITE_EDITS_DESCRIPTION,
    modType: "onsiteEdits",
    id: "content-experiment",
    iconComponent: Newspaper,
    iconColor: theme.igPrimitives.azure["A300"],
    iconBackgroundColor: theme.igPrimitives.azure[100],
    backgroundColor: theme.igPrimitives.azure[100],
  },
  {
    title: "Content: Split URL",
    description: SPLIT_URL_DESCRIPTION,
    modType: "splitUrl",
    id: "redirect-experiment",
    iconComponent: Split,
    iconColor: theme.igPrimitives.yellow["900"],
    iconBackgroundColor: theme.igPrimitives.yellow[50],
    backgroundColor: theme.igPrimitives.yellow[50],
  },
  {
    title: "Content: Template",
    description: TEMPLATE_DESCRIPTION,
    modType: "template",
    id: "template-experiment",
    iconComponent: LayoutPanelTop,
    iconColor: theme.igPrimitives.purple[900],
    iconBackgroundColor: theme.igPrimitives.purple[100],
    backgroundColor: theme.igPrimitives.purple[50],
  },
  {
    title: "Content: Theme",
    description: THEME_DESCRIPTION,
    modType: "theme",
    id: "theme-experiment",
    iconComponent: Palette,
    iconColor: theme.igPrimitives.pink["A100"],
    iconBackgroundColor: theme.igPrimitives.pink[100],
    backgroundColor: theme.igPrimitives.pink[50],
  },
  {
    title: "Price",
    description: PRICING_DESCRIPTION,
    modType: "price",
    id: "pricing-experiment",
    iconComponent: CircleDollarSign,
    iconColor: theme.igPrimitives.coral[900],
    iconBackgroundColor: theme.igPrimitives.coral[100],
    backgroundColor: theme.igPrimitives.coral[50],
  },
  {
    title: "Shipping",
    description: SHIPPING_DESCRIPTION,
    modType: "shipping",
    id: "shipping-experiment",
    iconComponent: Truck,
    iconColor: theme.igPrimitives.green["A200"],
    iconBackgroundColor: theme.igPrimitives.green[100],
    backgroundColor: theme.igPrimitives.green[50],
  },
  {
    title: "Offers",
    description: OFFER_DESCRIPTION,
    modType: "offer",
    id: "offers-experiment",
    iconComponent: Tag,
    iconColor: theme.igPrimitives.lime["A300"],
    iconBackgroundColor: theme.igPrimitives.lime[100],
    backgroundColor: theme.igPrimitives.lime[50],
  },
  {
    title: "Checkout",
    description: CHECKOUT_DESCRIPTION,
    modType: "checkoutBlock",
    id: "checkout-blocks-experiment",
    iconComponent: Shapes,
    iconColor: hasCorrectCheckoutPlan
      ? theme.igPrimitives.teal["A100"]
      : theme.palette.text.interactive.neutral.disabled,
    iconBackgroundColor: hasCorrectCheckoutPlan
      ? theme.igPrimitives.teal[50]
      : theme.igBackgroundColors.interactive.neutral.disabled,
    backgroundColor: hasCorrectCheckoutPlan
      ? theme.igPrimitives.teal[50]
      : theme.igBackgroundColors.interactive.neutral.default,
    tooltipText: !hasCorrectCheckoutPlan
      ? "This feature requires a Shopify Plus plan"
      : "",
  },
  {
    title: "Post-Purchase",
    description: "Create a one-step upsell offer shown after checkout.",
    modType: "postPurchase",
    id: "post-purchase-experiment",
    iconComponent: ShoppingCart,
    iconColor: theme.igPrimitives.pink[900],
    iconBackgroundColor: theme.igPrimitives.pink[100],
    backgroundColor: theme.igPrimitives.pink[50],
  },
];
